import React, { useState, useEffect, useCallback } from 'react';
import {
  Container,
  Paper,
  Typography,
  Box,
  Grid,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  CircularProgress,
  Pagination
} from '@mui/material';
import {
  Person as PersonIcon,
  Download as DownloadIcon,
  CalendarToday as CalendarIcon
} from '@mui/icons-material';
import { useAuth } from '../context/AuthContext';
import { downloadAPI } from '../services/api';
import { Navigate } from 'react-router-dom';

const Profile = () => {
  const { user, isAuthenticated } = useAuth();
  const [downloadHistory, setDownloadHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [pagination, setPagination] = useState({
    page: 1,
    totalPages: 1,
    total: 0
  });

  const fetchDownloadHistory = useCallback(async () => {
    try {
      const res = await downloadAPI.getHistory({ 
        page: pagination.page,
        limit: 10 
      });
      setDownloadHistory(res.data.data);
      setPagination({
        page: res.data.currentPage,
        totalPages: res.data.totalPages,
        total: res.data.total
      });
    } catch (error) {
      console.error('Error fetching download history:', error);
    } finally {
      setLoading(false);
    }
  }, [pagination.page]);

  useEffect(() => {
    if (isAuthenticated) {
      fetchDownloadHistory();
    }
  }, [isAuthenticated, fetchDownloadHistory]);

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h3" component="h1" gutterBottom>
        Mi Perfil
      </Typography>

      <Grid container spacing={3}>
        {/* Información del usuario */}
        <Grid item xs={12} md={4}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={2}>
              <PersonIcon sx={{ mr: 2, fontSize: 40 }} />
              <Box>
                <Typography variant="h5">{user?.username}</Typography>
                <Typography color="textSecondary">{user?.email}</Typography>
              </Box>
            </Box>
            
            <Divider sx={{ my: 2 }} />
            
            <Box>
              <Typography variant="body2" color="textSecondary">
                Rol
              </Typography>
              <Chip 
                label={user?.role === 'admin' ? 'Administrador' : 'Usuario'}
                color={user?.role === 'admin' ? 'primary' : 'default'}
                sx={{ mb: 2 }}
              />
              
              <Typography variant="body2" color="textSecondary">
                Miembro desde
              </Typography>
              <Typography variant="body1" sx={{ mb: 2 }}>
                {formatDate(user?.createdAt)}
              </Typography>
              
              {user?.lastLogin && (
                <>
                  <Typography variant="body2" color="textSecondary">
                    Último acceso
                  </Typography>
                  <Typography variant="body1">
                    {formatDate(user.lastLogin)}
                  </Typography>
                </>
              )}
            </Box>
          </Paper>
        </Grid>

        {/* Historial de descargas */}
        <Grid item xs={12} md={8}>
          <Paper elevation={3} sx={{ p: 3 }}>
            <Box display="flex" alignItems="center" mb={3}>
              <DownloadIcon sx={{ mr: 2 }} />
              <Typography variant="h5">
                Historial de Descargas
              </Typography>
            </Box>

            {loading ? (
              <Box display="flex" justifyContent="center" py={4}>
                <CircularProgress />
              </Box>
            ) : downloadHistory.length === 0 ? (
              <Typography color="textSecondary" align="center" py={4}>
                No tienes descargas registradas aún.
              </Typography>
            ) : (
              <>
                <List>
                  {downloadHistory.map((download, index) => (
                    <React.Fragment key={download.id || download._id}>
                      <ListItem alignItems="flex-start">
                        <ListItemText
                          primary={
                            <Box display="flex" justifyContent="space-between" alignItems="center">
                              <Typography variant="h6">
                                {download.software?.name || 'Software eliminado'}
                              </Typography>
                              <Box display="flex" alignItems="center">
                                <CalendarIcon sx={{ mr: 1, fontSize: 16 }} />
                                <Typography variant="body2" color="textSecondary">
                                  {formatDate(download.downloadDate)}
                                </Typography>
                              </Box>
                            </Box>
                          }
                          secondary={
                            <Box sx={{ mt: 1 }}>
                              {download.software && (
                                <>
                                  <Chip 
                                    label={`v${download.software.version}`}
                                    size="small"
                                    sx={{ mr: 1 }}
                                  />
                                  <Chip 
                                    label={download.software.category}
                                    size="small"
                                    variant="outlined"
                                  />
                                </>
                              )}
                            </Box>
                          }
                        />
                      </ListItem>
                      {index < downloadHistory.length - 1 && <Divider />}
                    </React.Fragment>
                  ))}
                </List>

                {/* Paginación */}
                {pagination.totalPages > 1 && (
                  <Box display="flex" justifyContent="center" mt={3}>
                    <Pagination
                      count={pagination.totalPages}
                      page={pagination.page}
                      onChange={(e, page) => setPagination({ ...pagination, page })}
                      color="primary"
                    />
                  </Box>
                )}

                <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                  <Typography variant="body2" color="textSecondary">
                    Total de descargas: {pagination.total}
                  </Typography>
                </Box>
              </>
            )}
          </Paper>
        </Grid>
      </Grid>
    </Container>
  );
};

export default Profile;